// FILE LOCATION: src/hooks/useSubscription.ts
// ──────────────────────────────────────────────────────────────
// Returns the user's effective tier (free | essential | pro).
//
// The effective tier is the HIGHER of:
//   - their Stripe subscription tier (from the `subscriptions` table)
//   - their highest active access grant tier (via the
//     get_active_grant_tier Postgres function)
//
// This means redeeming a code never DOWNGRADES a paying user, and
// a free user with an active grant gets the granted tier.
// ──────────────────────────────────────────────────────────────

import { useUser } from "@clerk/clerk-react";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";

export type Tier = "free" | "essential" | "pro";

const TIER_RANK: Record<Tier, number> = {
  free: 0,
  essential: 1,
  pro: 2,
};

const SUPABASE_FUNCTIONS_URL = `${
  import.meta.env.VITE_SUPABASE_URL
}/functions/v1`;

function maxTier(a: Tier, b: Tier): Tier {
  return TIER_RANK[a] >= TIER_RANK[b] ? a : b;
}

export function useSubscription() {
  const { user } = useUser();
  const [subscriptionTier, setSubscriptionTier] = useState<Tier>("free");
  const [grantTier, setGrantTier] = useState<Tier>("free");
  const [grantExpiresAt, setGrantExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    async function loadEffectiveTier(userId: string) {
      // Run both reads in parallel for speed.
      const [subResult, grantResult] = await Promise.all([
        supabase
          .from("subscriptions")
          .select("plan, status")
          .eq("user_id", userId)
          .in("status", ["active", "trialing"])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase.rpc("get_active_grant_tier", { p_user_id: userId }),
      ]);

      if (cancelled) return;

      // Subscription tier
      const subData = subResult.data;
      const nextSubTier: Tier =
        subData?.plan &&
        (subData.status === "active" || subData.status === "trialing")
          ? (subData.plan as Tier)
          : "free";

      // Grant tier — the rpc returns an array of rows (0 or 1).
      const grantRows = grantResult.data as
        | { granted_tier: Tier; expires_at: string }[]
        | null;
      const activeGrant =
        grantRows && grantRows.length > 0 ? grantRows[0] : null;

      setSubscriptionTier(nextSubTier);
      setGrantTier(activeGrant?.granted_tier ?? "free");
      setGrantExpiresAt(activeGrant?.expires_at ?? null);
      setLoading(false);
    }

    loadEffectiveTier(user.id);

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const checkout = useCallback(
    async (plan: "essential" | "pro") => {
      if (!user) return;

      const res = await fetch(
        `${SUPABASE_FUNCTIONS_URL}/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            plan,
            userId: user.id,
            userEmail: user.primaryEmailAddress?.emailAddress,
            returnUrl: window.location.origin,
          }),
        }
      );

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    },
    [user]
  );

  const manageSubscription = useCallback(async () => {
    if (!user) return;

    const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-portal-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        userId: user.id,
        returnUrl: window.location.origin,
      }),
    });

    const data = await res.json();
    if (data.url) {
      window.location.href = data.url;
    }
  }, [user]);

  const tier: Tier = maxTier(subscriptionTier, grantTier);

  // True when the user's effective tier comes from a grant rather than
  // a paid subscription. Useful for UI hints like "Access via promo code
  // — expires Jun 4, 2026".
  const isGrantAccess = TIER_RANK[grantTier] > TIER_RANK[subscriptionTier];

  return {
    tier,
    loading,
    isEssential: tier === "essential" || tier === "pro",
    isPro: tier === "pro",
    isGrantAccess,
    grantExpiresAt: isGrantAccess ? grantExpiresAt : null,
    checkout,
    manageSubscription,
  };
}
